// Angular
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GridsterItem } from 'angular-gridster2';
import { takeUntil } from 'rxjs';

// Models
import { UserUnitPeriodChannelDto } from '../../../../../channel/models/user-unit-period-channel-dto';
import { AppCleanup } from '../../../../../../shared/models';

// Services
import { JobService } from '../../../../../job/services/job.service';
import { ValidationService } from '../../../../../../shared/services/validation.service';

// Components
import { DashboardAlertDialogComponent } from '../dashboard-alert-dialog/dashboard-alert-dialog.component';

// Helpers
import { FormHelper } from '../../../../../../shared/helpers/form.helper';

@Component({
	selector: 'widget-edit-dialog',
	templateUrl: './widget-edit-dialog.component.html',
	styleUrls: ['./widget-edit-dialog.component.scss'],
})
export class WidgetEditDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string = 'Add widget';
	userJobChannel: UserUnitPeriodChannelDto;
	userJobChannels: UserUnitPeriodChannelDto[] = [];
	form: FormGroup;
	key: string;
	widgets: any[] = [
		{
			dashboardItemTypeId: 1,
			name: 'Gauge',
			enableChannel: true,
		},
		{
			dashboardItemTypeId: 2,
			name: 'Text Display',
			enableChannel: true,
		},
		{
			dashboardItemTypeId: 3,
			name: 'Vertical Gauge',
			enableChannel: true,
		},
		{
			dashboardItemTypeId: 4,
			name: 'Job Chart',
			enableChannel: false,
		},
		{
			dashboardItemTypeId: 5,
			name: 'Clock',
			enableChannel: false,
		},
		{
			dashboardItemTypeId: 6,
			name: 'Channel Display',
			enableChannel: false,
		},
		{
			dashboardItemTypeId: 7,
			name: 'Job Comments',
			enableChannel: false,
		},
	];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { key: string; gridsterItem: GridsterItem },
		public dialogRef: MatDialogRef<DashboardAlertDialogComponent>,
		private jobService: JobService,
		private formBuilder: FormBuilder
	) {
		super();

		this.key = data.key;
	}

	ngOnInit() {
		// Channel service should be loaded from parent already. If issues here, use observable.
		this.userJobChannels = this.jobService.findUserUnitPeriodChannelsForJob(this.key).filter((userUnitPeriodChannel) => userUnitPeriodChannel.options < 8);
		this.userJobChannel = this.userJobChannels.find((ujc) => ujc.unitPeriodChannel.channelId === this.data.gridsterItem.channelId);

		if (this.data?.gridsterItem?.id) {
			this.componentTitle = 'Edit widget';
		}

		this.form = this.formBuilder.group({
			selectedUserJobChannel: [this.userJobChannel],
			selectedWidgetType: [this.data.gridsterItem.dashboardItemTypeId, Validators.required],
			channelFontSize: [this.data.gridsterItem.channelFontSize, Validators.max(120)],
			valueFontSize: [this.data.gridsterItem.valueFontSize, Validators.max(120)],
			disableFlashing: [this.data.gridsterItem.disableFlashing],
			disableSounds: [this.data.gridsterItem.disableSounds],
			min: [this.data.gridsterItem.min, [Validators.required, Validators.max(999999999), ValidationService.decimalValidation(6)]],
			max: [this.data.gridsterItem.max, [Validators.required, Validators.max(999999999), ValidationService.decimalValidation(6)]],
		});

		this.form
			.get('selectedWidgetType')
			.valueChanges.pipe(takeUntil(this.destroy))
			.subscribe((valueChange) => {
				this.data.gridsterItem.dashboardItemTypeId = valueChange;

				if ([2, 4, 5, 6, 7].includes(this.data.gridsterItem.dashboardItemTypeId)) {
					this.form.controls['min'].clearValidators();
					this.form.controls['max'].clearValidators();
				} else {
					this.form.controls['min'].setValidators([Validators.required, Validators.max(999999999), ValidationService.decimalValidation(6)]);
					this.form.controls['max'].setValidators([Validators.required, Validators.max(999999999), ValidationService.decimalValidation(6)]);
				}

				this.form.controls['min'].updateValueAndValidity();
				this.form.controls['max'].updateValueAndValidity();
			});

		this.form
			.get('selectedUserJobChannel')
			.valueChanges.pipe(takeUntil(this.destroy))
			.subscribe((valueChange) => {
				this.userJobChannel = this.userJobChannels.find((ujc) => ujc.unitPeriodChannel.channelId === valueChange.unitPeriodChannel?.channelId);

				if (this.userJobChannel) {
					if (this.form.get('min').value === null) {
						this.form.get('min').setValue(this.userJobChannel.minY);
					}

					if (this.form.get('max').value === null) {
						this.form.get('max').setValue(this.userJobChannel.maxY);
					}
				}
			});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	submit() {
		this.data.gridsterItem.channelFontSize = this.form.get('channelFontSize').value;
		this.data.gridsterItem.valueFontSize = this.form.get('valueFontSize').value;
		this.data.gridsterItem.disableFlashing = this.form.get('disableFlashing').value;
		this.data.gridsterItem.disableSounds = this.form.get('disableSounds').value;
		this.data.gridsterItem.min = this.form.get('min').value;
		this.data.gridsterItem.max = this.form.get('max').value;
		this.data.gridsterItem.channelId = this.form.get('selectedUserJobChannel').value?.unitPeriodChannel.channelId;
		this.data.gridsterItem.dashboardItemTypeId = this.form.get('selectedWidgetType').value;

		this.dialogRef.close(this.data.gridsterItem);
	}
}
